<script setup>
import { computed, ref, watch } from "vue";
import flatPickr from "vue-flatpickr-component";
import {
  DEFAULT_TIMEZONE,
  TIMEZONE_OPTIONS,
} from "@/views/analysis/analysisOptions";
import { toDateInputValue } from "@/views/analysis/analysisFormat";
import {
  MAX_HOURLY_RANGE_DAYS,
  PRESETS,
  defaultGranularityForPreset,
  rangeDayCount,
  resolvePresetRange,
  todayInTimezone,
} from "../adStatsTime";

const props = defineProps({
  minDate: { type: String, required: true },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(["refresh"]);

const preset = defineModel("preset", { type: String, required: true });
const dateFrom = defineModel("dateFrom", { type: String, required: true });
const dateTo = defineModel("dateTo", { type: String, required: true });
const timezone = defineModel("timezone", { type: String, required: true });
const granularity = defineModel("granularity", {
  type: String,
  required: true,
});
const compare = defineModel("compare", { type: Boolean, required: true });

const timezoneOptions = [...new Set([DEFAULT_TIMEZONE, ...TIMEZONE_OPTIONS])];

const today = computed(() => todayInTimezone(timezone.value));
const hourlyAllowed = computed(
  () => rangeDayCount(dateFrom.value, dateTo.value) <= MAX_HOURLY_RANGE_DAYS,
);
const compareAllowed = computed(() => preset.value !== "all");

const pickerValue = ref([dateFrom.value, dateTo.value]);
const pickerConfig = computed(() => ({
  mode: "range",
  dateFormat: "Y-m-d",
  maxDate: today.value,
}));

const applyPresetRange = (key) => {
  const range = resolvePresetRange(key, timezone.value, props.minDate);
  dateFrom.value = range.dateFrom;
  dateTo.value = range.dateTo;
};

const selectPreset = (key) => {
  preset.value = key;
  if (key === "custom") return;
  applyPresetRange(key);
  granularity.value = defaultGranularityForPreset(key);
};

const onPickerChange = (selectedDates) => {
  if (!Array.isArray(selectedDates) || selectedDates.length < 2) return;
  const from = toDateInputValue(selectedDates[0]);
  const to = toDateInputValue(selectedDates[1]);
  if (from === dateFrom.value && to === dateTo.value) return;
  preset.value = "custom";
  dateFrom.value = from;
  dateTo.value = to;
};

watch([dateFrom, dateTo], ([from, to]) => {
  pickerValue.value = [from, to];
});

watch(timezone, () => {
  if (preset.value !== "custom") applyPresetRange(preset.value);
});

watch(
  hourlyAllowed,
  (allowed) => {
    if (!allowed && granularity.value === "hour") granularity.value = "day";
  },
  { immediate: true },
);

watch(
  compareAllowed,
  (allowed) => {
    if (!allowed) compare.value = false;
  },
  { immediate: true },
);
</script>

<template>
  <div class="card shadow-sm mb-4">
    <div class="card-body py-3">
      <div class="row g-3 align-items-end">
        <div class="col-12 col-xl-auto">
          <label class="form-label text-xs fw-semibold mb-1">Period</label>
          <div class="btn-group btn-group-sm d-flex flex-wrap" role="group">
            <button
              v-for="option in PRESETS"
              :key="option.key"
              type="button"
              class="btn mb-0"
              :class="
                option.key === preset ? 'btn-primary' : 'btn-outline-secondary'
              "
              @click="selectPreset(option.key)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div class="col-12 col-sm-6 col-xl-3">
          <label class="form-label text-xs fw-semibold mb-1">Date range</label>
          <flat-pickr
            v-model="pickerValue"
            class="form-control form-control-sm"
            :config="pickerConfig"
            @on-change="onPickerChange"
          />
        </div>

        <div class="col-6 col-sm-3 col-xl-auto">
          <label class="form-label text-xs fw-semibold mb-1">Timezone</label>
          <select v-model="timezone" class="form-select form-select-sm">
            <option v-for="zone in timezoneOptions" :key="zone" :value="zone">
              {{ zone }}
            </option>
          </select>
        </div>

        <div class="col-6 col-sm-3 col-xl-auto">
          <label class="form-label text-xs fw-semibold mb-1">Group by</label>
          <div class="btn-group btn-group-sm d-flex" role="group">
            <button
              type="button"
              class="btn mb-0"
              :class="
                granularity === 'hour' ? 'btn-primary' : 'btn-outline-secondary'
              "
              :disabled="!hourlyAllowed"
              :title="
                hourlyAllowed
                  ? ''
                  : `Hourly view is limited to ${MAX_HOURLY_RANGE_DAYS} days`
              "
              @click="granularity = 'hour'"
            >
              Hour
            </button>
            <button
              type="button"
              class="btn mb-0"
              :class="
                granularity === 'day' ? 'btn-primary' : 'btn-outline-secondary'
              "
              @click="granularity = 'day'"
            >
              Day
            </button>
          </div>
        </div>

        <div class="col-12 col-sm-auto d-flex align-items-center gap-3 ms-xl-auto">
          <div class="form-check form-switch mb-0">
            <input
              id="adStatsCompare"
              v-model="compare"
              class="form-check-input"
              type="checkbox"
              :disabled="!compareAllowed"
            />
            <label class="form-check-label text-sm" for="adStatsCompare">
              Compare to previous period
            </label>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-success mb-0"
            :disabled="loading"
            @click="emit('refresh')"
          >
            <i class="fas fa-sync me-2"></i>
            {{ loading ? "Refreshing..." : "Refresh" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
